import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { organizationAPI, departmentAPI, transactionAPI, notificationAPI, reportAPI } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Building2, Wallet, Layers, ClipboardCheck, TrendingUp, 
  ArrowUpRight, ArrowDownRight, Bell, CheckCircle, XCircle,
  Clock, AlertTriangle, Eye
} from 'lucide-react';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';

const CHART_COLORS = ['#1E3A8A', '#3B82F6', '#60A5FA', '#93C5FD', '#BFDBFE'];

const AdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [org, setOrg] = useState(null);
  const [stats, setStats] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [spendingTrends, setSpendingTrends] = useState([]);
  const [deptSpending, setDeptSpending] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [orgRes, statsRes, deptRes, pendingRes, notifRes, trendsRes, deptSpendRes] = await Promise.all([
        organizationAPI.get(),
        organizationAPI.getStats(),
        departmentAPI.list(),
        transactionAPI.getPending(),
        notificationAPI.list(false),
        reportAPI.getSpendingTrends('daily', null, 14),
        reportAPI.getSpendingByDepartment()
      ]);

      setOrg(orgRes.data);
      setStats(statsRes.data);
      setDepartments(deptRes.data);
      setPendingApprovals(pendingRes.data.slice(0, 5));
      setNotifications(notifRes.data.slice(0, 5));
      setSpendingTrends(trendsRes.data);
      setDeptSpending(deptSpendRes.data);
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const styles = {
      'PENDING_APPROVAL': 'status-pending',
      'COMPLETED': 'status-success',
      'REJECTED': 'status-error',
      'PROCESSING': 'status-info',
      'INFO_REQUESTED': 'status-pending'
    };
    return <Badge className={`${styles[status] || 'status-info'} rounded-full`}>{status.replace('_', ' ')}</Badge>;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-dashboard">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 font-['Outfit']">
              Welcome back, {org?.name || 'Admin'}
            </h1>
            <p className="text-gray-500 mt-1">Here's what's happening with your organization today</p>
          </div>
          <Link to="/admin/departments">
            <Button className="bg-blue-900 hover:bg-blue-800 text-white gap-2 shadow-lg shadow-blue-900/20">
              <Layers className="w-4 h-4" /> New Department
            </Button>
          </Link>
        </div>

        {/* Stats Cards - Bento Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="glass-card col-span-2 md:col-span-1 animate-fadeInUp">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Wallet className="w-6 h-6 text-blue-700" />
                </div>
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
              <p className="text-gray-500 text-sm">Total Balance</p>
              <p className="text-2xl font-bold text-gray-900 font-['JetBrains_Mono'] mt-1">
                {formatCurrency(stats?.total_balance)}
              </p>
            </CardContent>
          </Card>

          <Card className="glass-card animate-fadeInUp delay-100">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                  <Layers className="w-5 h-5 text-indigo-600" />
                </div>
              </div>
              <p className="text-gray-500 text-sm">Departments</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stats?.department_count || 0}</p>
            </CardContent>
          </Card>

          <Card className="glass-card animate-fadeInUp delay-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <ClipboardCheck className="w-5 h-5 text-amber-600" />
                </div>
                {stats?.pending_approvals > 0 && (
                  <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
                )}
              </div>
              <p className="text-gray-500 text-sm">Pending Approvals</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stats?.pending_approvals || 0}</p>
            </CardContent>
          </Card>

          <Card className="glass-card animate-fadeInUp delay-300">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <ArrowUpRight className="w-5 h-5 text-green-600" />
                </div>
              </div>
              <p className="text-gray-500 text-sm">Total Spent</p>
              <p className="text-2xl font-bold text-gray-900 font-['JetBrains_Mono'] mt-1">
                {formatCurrency(stats?.total_spent)}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-12 gap-6">
          {/* Spending Trends Chart */}
          <Card className="glass-card col-span-12 lg:col-span-8">
            <CardHeader className="border-b border-gray-200 pb-4">
              <CardTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                Spending Trends (Last 14 Days)
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="h-64">
                {spendingTrends.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={spendingTrends}>
                      <defs>
                        <linearGradient id="colorAmount" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#1E3A8A" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#1E3A8A" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis 
                        dataKey="date" 
                        stroke="#9CA3AF" 
                        tick={{ fill: '#6B7280', fontSize: 12 }}
                        tickFormatter={(val) => val.split('-').slice(1).join('/')}
                      />
                      <YAxis 
                        stroke="#9CA3AF" 
                        tick={{ fill: '#6B7280', fontSize: 12 }}
                        tickFormatter={(val) => `$${val / 1000}k`}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          background: 'rgba(255,255,255,0.95)', 
                          border: '1px solid #E5E7EB',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        }}
                        formatter={(val) => [`$${val.toLocaleString()}`, 'Amount']}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="amount" 
                        stroke="#1E3A8A" 
                        fillOpacity={1} 
                        fill="url(#colorAmount)" 
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-400">
                    No spending data yet
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Department Spending Pie Chart */}
          <Card className="glass-card col-span-12 lg:col-span-4">
            <CardHeader className="border-b border-gray-200 pb-4">
              <CardTitle className="text-lg font-semibold text-gray-900">Spending by Department</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="h-64">
                {deptSpending.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={deptSpending}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey="total_spent"
                        nameKey="department_name"
                      >
                        {deptSpending.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          background: 'rgba(255,255,255,0.95)', 
                          border: '1px solid #E5E7EB',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        }}
                        formatter={(val) => [`$${val.toLocaleString()}`, 'Spent']}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-400">
                    No department data
                  </div>
                )}
              </div>
              {/* Legend */}
              <div className="space-y-2 mt-4">
                {deptSpending.slice(0, 4).map((dept, index) => (
                  <div key={dept.department_id} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                      />
                      <span className="text-gray-600">{dept.department_name}</span>
                    </div>
                    <span className="text-gray-900 font-['JetBrains_Mono']">
                      {formatCurrency(dept.total_spent)}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Pending Approvals */}
          <Card className="glass-card col-span-12 lg:col-span-6">
            <CardHeader className="border-b border-gray-200 pb-4 flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <ClipboardCheck className="w-5 h-5 text-amber-500" />
                Pending Approvals
              </CardTitle>
              <Link to="/admin/approvals">
                <Button variant="ghost" size="sm" className="text-blue-700 hover:text-blue-600">
                  View All <ArrowUpRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent className="pt-4">
              {pendingApprovals.length > 0 ? (
                <div className="space-y-3">
                  {pendingApprovals.map((txn) => (
                    <div 
                      key={txn.transaction_id}
                      className="flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                          <Clock className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{txn.beneficiary_name}</p>
                          <p className="text-xs text-gray-500">{txn.department_name}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-gray-900 font-['JetBrains_Mono']">
                          {formatCurrency(txn.amount)}
                        </p>
                        {getStatusBadge(txn.status)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <CheckCircle className="w-12 h-12 text-blue-200 mb-3" />
                  <p className="text-gray-400">No pending approvals</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Notifications */}
          <Card className="glass-card col-span-12 lg:col-span-6">
            <CardHeader className="border-b border-gray-200 pb-4">
              <CardTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Bell className="w-5 h-5 text-blue-500" />
                Recent Notifications
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              {notifications.length > 0 ? (
                <div className="space-y-3">
                  {notifications.map((notif) => (
                    <div 
                      key={notif.notification_id}
                      className={`flex items-start gap-3 p-3 rounded-lg ${
                        notif.is_read ? 'bg-gray-50' : 'bg-blue-50 border-l-2 border-blue-600'
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        notif.notification_type.includes('APPROVED') ? 'bg-green-100' :
                        notif.notification_type.includes('REJECTED') ? 'bg-red-100' :
                        'bg-blue-100'
                      }`}>
                        {notif.notification_type.includes('APPROVED') ? (
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        ) : notif.notification_type.includes('REJECTED') ? (
                          <XCircle className="w-4 h-4 text-red-600" />
                        ) : (
                          <Bell className="w-4 h-4 text-blue-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">{notif.title}</p>
                        <p className="text-xs text-gray-500 truncate">{notif.message}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <Bell className="w-12 h-12 text-gray-200 mb-3" />
                  <p className="text-gray-400">No notifications</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Departments Overview */}
          <Card className="glass-card col-span-12">
            <CardHeader className="border-b border-gray-200 pb-4 flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Layers className="w-5 h-5 text-blue-600" />
                Departments Overview
              </CardTitle>
              <Link to="/admin/departments">
                <Button variant="ghost" size="sm" className="text-blue-700 hover:text-blue-600">
                  Manage <ArrowUpRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent className="pt-4">
              {departments.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-sm text-gray-500 border-b border-gray-200">
                        <th className="pb-3 font-medium">Department</th>
                        <th className="pb-3 font-medium">Head</th>
                        <th className="pb-3 font-medium">Balance</th>
                        <th className="pb-3 font-medium">Virtual Account</th>
                        <th className="pb-3 font-medium">Status</th>
                        <th className="pb-3 font-medium"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {departments.slice(0, 5).map((dept) => (
                        <tr key={dept.department_id} className="border-b border-gray-100 table-row-hover">
                          <td className="py-4">
                            <p className="font-medium text-gray-900">{dept.name}</p>
                            <p className="text-xs text-gray-500">{dept.description || 'No description'}</p>
                          </td>
                          <td className="py-4 text-gray-600">{dept.department_head_name || 'Unassigned'}</td>
                          <td className="py-4">
                            <span className="font-['JetBrains_Mono'] text-gray-900">
                              {formatCurrency(dept.balance)}
                            </span>
                          </td>
                          <td className="py-4">
                            <p className="font-['JetBrains_Mono'] text-sm text-gray-500">
                              {dept.virtual_account?.account_number || 'N/A'}
                            </p>
                          </td>
                          <td className="py-4">
                            <Badge className={dept.is_active ? 'status-success' : 'status-error'}>
                              {dept.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                          <td className="py-4">
                            <Link to={`/admin/departments/${dept.department_id}`}>
                              <Button variant="ghost" size="sm" className="text-gray-400 hover:text-gray-900">
                                <Eye className="w-4 h-4" />
                              </Button>
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <Layers className="w-16 h-16 text-gray-200 mb-4" />
                  <p className="text-gray-500 mb-4">No departments yet</p>
                  <Link to="/admin/departments">
                    <Button className="bg-blue-900 hover:bg-blue-800 text-white shadow-lg shadow-blue-900/20">
                      Create First Department
                    </Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AdminDashboard;
